import {
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';

import {fabric} from "fabric";
import 'fabric-history';
import {ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR} from "@angular/forms";
import {Model} from "@app/strain/config/gages/models/model.model";

@Component({
    selector: 'app-drawing-canvas',
    templateUrl: './drawing-canvas.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: DrawingCanvasComponent,
            multi: true
        }
    ],
    styleUrls: ['./drawing-canvas.component.scss']
})
export class DrawingCanvasComponent implements OnInit, OnChanges, ControlValueAccessor {
    @ViewChild('uploader', {static: true}) uploader: ElementRef;
    @ViewChild('paint', {static: true}) paint: ElementRef;
    @ViewChild('unidirectional', {static: true}) unidirectional: TemplateRef<any>;
    @ViewChild('bidirectional', {static: true}) bidirectional: TemplateRef<any>;
    @ViewChild('tridirectional', {static: true}) tridirectional: TemplateRef<any>;

    @Input() src = '';

    @Input() showErrors = false;
    @Input() formControl: UntypedFormControl;
    @Input() arrows: any;

    width = 700;
    height = 490;
    canvas: any;
    downloadLink = null;
    _processedImageURL = '';
    focused = false;

    @Output() imageProcessed = new EventEmitter<any>();

    @HostBinding('class.empty') isEmpty = true;

    private onChangeCallback: (_: any) => {};
    private onTouchedCallback: (_: any) => {};

    @HostListener('mouseleave') mouseleave(event: Event) {
        // Autosave
        if (!this.isEmpty) {
            this.value = this.canvas.toDataURL({
                format: 'jpeg',
                quality: 0.8
            });

            this.canvas.discardActiveObject().renderAll();
        }
    }

    @HostListener('blur') blur(event: Event) {
        this.canvas.discardActiveObject().renderAll();
    }

    @HostListener('mousedown') mousedown(event: Event) {
        this.canvas.on('object:rotating', function (e) {
            const actObj = e.target;
            actObj._objects.forEach(function (obj) {
                if (obj.type === 'text') {
                    obj.set('angle', -actObj.angle);
                    obj.setCoords();
                }
            });
        });
        this.canvas.renderAll();
    }

    constructor() {

    }

    ngOnInit() {
        this.initCanvas();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.src) {
            this.initCanvas();

            this.isEmpty = !this.src;
            this.onClearCanvas();
            if (!this.isEmpty) {
                this.preloadImageToCanvas();
            } else {
                this.resetImage();
            }
        }
    }


    getArrowType(gageType) {
        switch (+gageType) {
            case Model.Unidirectional:
                return this.unidirectional;
            case Model.Bidirectional:
                return this.bidirectional;
            case Model.Tridirectional:
                return this.tridirectional;
        }
    }


    dragElement(e) {
        e.dataTransfer.setData("id", e.target.id);
    }

    dropElement(e) {
        e.preventDefault();
        const data = e.dataTransfer.getData("id");
        const imag = document.getElementById(data) as any;
        const pointer = this.canvas.getPointer(e);
        const markerIndex = parseInt(data.match(/\d+$/), 10).toString();
        const badgeRadius = 15;

        const badge = new fabric.Circle({
            radius: badgeRadius,
            left: 0,
            top: 0,
            fill: '#ffffff',
            stroke: '#1f2937',
            strokeWidth: 2,
            opacity: 0.95
        });
        const text = new fabric.Text(markerIndex, {
            left: badgeRadius,
            top: badgeRadius,
            originX: 'center',
            originY: 'center',
            fontSize: 20,
            fontWeight: '600',
            fill: '#1f2937'
        });
        const img = new fabric.Image(imag, {
            left: badgeRadius * 2 + 6,
            top: 6,
            crossOrigin: 'anonymous'
        });
        img.scaleToWidth(imag.width);
        img.scaleToHeight(imag.height);
        const group = new fabric.Group([badge, text, img], {
            left: pointer.x,
            top: pointer.y,
        });
        this.canvas.add(group);
        this.canvas.bringToFront(group);
    }

    downloadImage(e) {
        e.preventDefault()
        this.downloadLink = this.createDownloadLink();
        this.downloadLink.click();

        return false;
    }

    resetImage(e: any = null) {
        e?.preventDefault()
        this.onClearCanvas();
        this.isEmpty = true;
        this.value = '';
    }


    undo(event: any) {
        event.preventDefault();
        this.canvas.undo();
        return false;
    }

    onUpload(file: any) {
        this.isEmpty = false;
        this.onClearCanvas();
        const reader = new FileReader();
        reader.onload = (e: any) => {
            const image = new Image();
            image.src = e.target.result;
            image.onload = () => {
                const img = new fabric.Image(image);
                this.fitImageToCanvas(img);
                this.canvas.add(img).setActiveObject(img).renderAll();
            }
        }
        reader.readAsDataURL(file);
    }

    handlePaste(pasteEvent) {
        if (pasteEvent.clipboardData === false) {
            return;
        }

        const items = pasteEvent.clipboardData.items;
        if (items === undefined) {
            return;
        }

        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf("image") === -1) {
                continue;
            }

            const blob = items[i].getAsFile();

            this.onUpload(blob);
        }
    }

    onClearCanvas() {
        if (this.canvas) {
            this.canvas.getObjects().forEach((o) => {
                this.canvas.remove(o);
            })
            this.canvas.setBackgroundColor('#ffffff', () => undefined);
            this.canvas.discardActiveObject().renderAll();
        }
    }

    redo($event: any) {
        event.preventDefault();
        this.canvas.redo();
        return false;
    }

    writeValue(value) {
        this._processedImageURL = value;
    }

    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }

    onClick() {
        this.focused = true;
    }

    onBlur() {
        this.focused = false;
    }


    private preloadImageToCanvas() {
        fabric.Image.fromURL(this.src, (oImg) => {
            this.fitImageToCanvas(oImg);
            this.canvas.add(oImg);
        }, {crossOrigin: 'anonymous'});
    }

    private initCanvas() {
        if (!this.canvas) {
            this.canvas = new fabric.Canvas(this.paint.nativeElement, {
                backgroundColor: '#ffffff'
            });
        }

        this.canvas.setWidth(this.width);
        this.canvas.setHeight(this.height);
        this.canvas.setBackgroundColor('#ffffff', () => undefined);
        this.canvas.renderAll();
    }

    private fitImageToCanvas(image: fabric.Image) {
        const imageWidth = image.width || 1;
        const imageHeight = image.height || 1;
        const scale = Math.min(this.canvas.width / imageWidth, this.canvas.height / imageHeight);
        const scaledWidth = imageWidth * scale;
        const scaledHeight = imageHeight * scale;

        image.set({
            left: (this.canvas.width - scaledWidth) / 2,
            top: (this.canvas.height - scaledHeight) / 2,
            scaleX: scale,
            scaleY: scale
        });
    }


    private createDownloadLink() {
        const downloadLink = document.createElement('a');
        downloadLink.download = 'canvas.png';
        downloadLink.href = this._processedImageURL;
        return downloadLink;
    }

    get value() {
        return this._processedImageURL;
    }

    set value(value) {
        this._processedImageURL = value;

        if (this.onChangeCallback) {
            this.onChangeCallback(value);
        }

        this.imageProcessed.emit(value);
    }
}
